/**
 * ============================================================================
 * TOKEN CONTEXT v1.2 - ZENTRALES TOKEN-MANAGEMENT MIT AUTO-REFRESH
 * ============================================================================
 *
 * Löst das Problem: Jede Komponente ruft getAccessToken() auf → Re-Render Loop
 *
 * Lösung: Token wird 1x in App.js geladen und über Context bereitgestellt.
 * Komponenten verwenden useToken() statt useAuth().getAccessToken()
 *
 * ✅ v1.2: Auto-Refresh Timer + authFetch Wrapper
 * ✅ v1.1: Verwendet useAuth statt useLogto für Native-Unterstützung
 *
 * @version 1.2.0
 */

import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
// ✅ v1.1: useAuth statt useLogto für Native-Unterstützung
import { useAuth } from '../hooks/useAuth';

console.log('🔐 TokenContext v1.2 loaded - AUTO-REFRESH + authFetch');

// ============================================================================
// CONFIGURATION
// ============================================================================
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const LOGTO_API_RESOURCE = process.env.REACT_APP_LOGTO_API_RESOURCE || API_URL;

// ============================================================================
// GLOBAL STATE (außerhalb React für Stabilität)
// ============================================================================
let GLOBAL_TOKEN = null;
let GLOBAL_TOKEN_LOADING = false;
let GLOBAL_TOKEN_PROMISE = null;
let GLOBAL_TOKEN_TIMESTAMP = null;
const TOKEN_CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten

// ============================================================================
// JWT HELPER - Token-Ablaufzeit parsen
// ============================================================================
function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

// ============================================================================
// CONTEXT
// ============================================================================
const TokenContext = createContext(null);

// ============================================================================
// PROVIDER
// ============================================================================
export function TokenProvider({ children }) {
  // ✅ v1.1: useAuth statt useLogto
  const { getAccessToken, isAuthenticated } = useAuth();

  const [token, setToken] = useState(() => GLOBAL_TOKEN);
  const [loading, setLoading] = useState(() => !GLOBAL_TOKEN);
  const [error, setError] = useState(null);

  // Refs für stabile Funktionen
  const getAccessTokenRef = useRef(getAccessToken);
  const isMountedRef = useRef(true);
  const refreshTimerRef = useRef(null);
  const tokenRef = useRef(GLOBAL_TOKEN);

  // Update refs
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  // Sync tokenRef
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Track mount + cleanup timer
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  // ============================================================================
  // AUTO-REFRESH TIMER - Token erneuern BEVOR er abläuft
  // ============================================================================
  const scheduleRefresh = useCallback((newToken) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const expiry = getTokenExpiry(newToken);
    if (!expiry) return;

    const refreshIn = expiry - Date.now() - 60000; // 60s vor Ablauf
    if (refreshIn <= 0) {
      console.log('🔐 TokenContext: Token already near expiry, refreshing now...');
      // Token fast abgelaufen — sofort erneuern
      GLOBAL_TOKEN = null;
      GLOBAL_TOKEN_TIMESTAMP = null;
      GLOBAL_TOKEN_PROMISE = null;
      // Trigger re-load via getAccessToken
      (async () => {
        try {
          const fresh = await getAccessTokenRef.current(LOGTO_API_RESOURCE);
          GLOBAL_TOKEN = fresh;
          GLOBAL_TOKEN_TIMESTAMP = Date.now();
          if (isMountedRef.current) setToken(fresh);
          scheduleRefresh(fresh);
        } catch (err) {
          console.error('❌ TokenContext: Immediate refresh failed:', err);
        }
      })();
      return;
    }

    const refreshMinutes = Math.round(refreshIn / 60000);
    console.log(`🔐 TokenContext: Auto-refresh scheduled in ${refreshMinutes}min`);

    refreshTimerRef.current = setTimeout(async () => {
      if (!isMountedRef.current) return;

      console.log('🔐 TokenContext: Auto-refreshing token...');
      GLOBAL_TOKEN = null;
      GLOBAL_TOKEN_TIMESTAMP = null;
      GLOBAL_TOKEN_PROMISE = null;

      try {
        const fresh = await getAccessTokenRef.current(LOGTO_API_RESOURCE);
        GLOBAL_TOKEN = fresh;
        GLOBAL_TOKEN_TIMESTAMP = Date.now();
        if (isMountedRef.current) setToken(fresh);
        console.log('🔐 TokenContext: Auto-refresh successful');
        scheduleRefresh(fresh);
      } catch (err) {
        console.error('❌ TokenContext: Auto-refresh failed:', err);
      }
    }, refreshIn);
  }, []);

  // ============================================================================
  // TOKEN LOADING - NUR EINMAL!
  // ============================================================================
  useEffect(() => {
    const loadToken = async () => {
      // Nicht authentifiziert?
      if (!isAuthenticated) {
        console.log('🔐 TokenContext: Not authenticated');
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        if (isMountedRef.current) {
          setLoading(false);
          setToken(null);
        }
        return;
      }

      // Token noch gültig?
      if (GLOBAL_TOKEN && GLOBAL_TOKEN_TIMESTAMP) {
        const age = Date.now() - GLOBAL_TOKEN_TIMESTAMP;
        if (age < TOKEN_CACHE_DURATION) {
          console.log('🔐 TokenContext: Using cached token (age: ' + Math.round(age/1000) + 's)');
          if (isMountedRef.current) {
            setToken(GLOBAL_TOKEN);
            setLoading(false);
          }
          return;
        }
      }

      // Bereits am Laden?
      if (GLOBAL_TOKEN_PROMISE) {
        console.log('🔐 TokenContext: Waiting for existing load...');
        try {
          const result = await GLOBAL_TOKEN_PROMISE;
          if (isMountedRef.current) {
            setToken(result);
            setLoading(false);
          }
        } catch (err) {
          console.error('🔐 TokenContext: Shared promise failed:', err);
          if (isMountedRef.current) {
            setError(err.message);
            setLoading(false);
          }
        }
        return;
      }

      // Neues Token laden
      console.log('🔐 TokenContext: Loading new token...');

      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }

      const fetchPromise = (async () => {
        const newToken = await getAccessTokenRef.current(LOGTO_API_RESOURCE);
        return newToken;
      })();

      GLOBAL_TOKEN_PROMISE = fetchPromise;

      try {
        const newToken = await fetchPromise;

        GLOBAL_TOKEN = newToken;
        GLOBAL_TOKEN_TIMESTAMP = Date.now();

        console.log('🔐 TokenContext: Token loaded successfully');

        if (isMountedRef.current) {
          setToken(newToken);
        }

        // ✅ v1.2: Auto-Refresh Timer starten
        scheduleRefresh(newToken);

        return newToken;

      } catch (err) {
        console.error('❌ TokenContext: Error:', err);

        if (isMountedRef.current) {
          setError(err.message);
        }

        throw err;

      } finally {
        GLOBAL_TOKEN_PROMISE = null;

        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    loadToken();
  }, [isAuthenticated, scheduleRefresh]);

  // ============================================================================
  // REFRESH TOKEN FUNCTION
  // ============================================================================
  const refreshToken = useMemo(() => async () => {
    if (!isAuthenticated) return null;

    console.log('🔐 TokenContext: Refreshing token...');

    // Cache invalidieren
    GLOBAL_TOKEN = null;
    GLOBAL_TOKEN_TIMESTAMP = null;

    try {
      const newToken = await getAccessTokenRef.current(LOGTO_API_RESOURCE);
      GLOBAL_TOKEN = newToken;
      GLOBAL_TOKEN_TIMESTAMP = Date.now();
      setToken(newToken);
      // ✅ v1.2: Neuen Timer starten
      scheduleRefresh(newToken);
      return newToken;
    } catch (err) {
      console.error('❌ TokenContext: Refresh failed:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, scheduleRefresh]);

  // ============================================================================
  // AUTH FETCH - Globaler Wrapper mit 401-Retry
  // ============================================================================
  const authFetch = useCallback(async (url, options = {}) => {
    const makeHeaders = (t) => ({
      'Content-Type': 'application/json',
      ...options.headers,
      ...(t ? { 'Authorization': `Bearer ${t}` } : {})
    });

    let response = await fetch(url, { ...options, headers: makeHeaders(tokenRef.current) });

    // Bei 401: Token einmal refreshen und Request wiederholen
    if (response.status === 401 && tokenRef.current) {
      try {
        const newToken = await refreshToken();
        if (newToken) {
          response = await fetch(url, { ...options, headers: makeHeaders(newToken) });
        }
      } catch {
        // Refresh fehlgeschlagen — Original-Response zurückgeben
      }
    }

    return response;
  }, [refreshToken]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  const contextValue = useMemo(() => ({
    token,
    loading,
    error,
    isAuthenticated,
    refreshToken,
    authFetch,

    // Helper für API-Calls
    getAuthHeaders: () => token ? {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    } : {
      'Content-Type': 'application/json'
    }
  }), [token, loading, error, isAuthenticated, refreshToken, authFetch]);

  return (
    <TokenContext.Provider value={contextValue}>
      {children}
    </TokenContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================
export function useToken() {
  const context = useContext(TokenContext);

  if (!context) {
    console.warn('⚠️ useToken must be used within a TokenProvider');
    return {
      token: null,
      loading: false,
      error: 'TokenProvider not found',
      isAuthenticated: false,
      refreshToken: () => Promise.resolve(null),
      authFetch: (url, opts) => fetch(url, opts),
      getAuthHeaders: () => ({ 'Content-Type': 'application/json' })
    };
  }

  return context;
}

// ============================================================================
// EXPORTS
// ============================================================================
export default TokenContext;
